<template>
  <v-card variant="tonal" class="mb-3" :style="{ borderLeft: `4px solid ${eventColor}` }">
    <v-card-title class="cra-header">
      <!-- Single-day event: show time range -->
      <div v-if="!craEvent._isMultiDay" class="cra-range">
        <div class="cra-datetime cra-first">
          <span class="cra-date">{{ dateHandler.convertUnixTimestampToHumanTime(craEvent.date_start) }}</span>
        </div>
        <div class="cra-arrow-wrapper">
          <v-icon :icon="mdiArrowRight" size="x-small" class="cra-arrow" />
        </div>
        <div class="cra-datetime">
          <span class="cra-date">{{ dateHandler.convertUnixTimestampToHumanTime(craEvent.date_end) }}</span>
        </div>
      </div>
      <!-- Multi-day event: keep the same start/end date-time display on every day -->
      <div v-else class="cra-range">
        <div class="cra-datetime cra-first">
          <span class="cra-date">{{ dateHandler.convertUnixTimestampToHumanDate(craEvent.date_start) }}</span>
          <span class="cra-time">{{ dateHandler.convertUnixTimestampToHumanTime(craEvent.date_start) }}</span>
        </div>
        <div class="cra-arrow-wrapper">
          <v-icon :icon="mdiArrowRight" size="x-small" class="cra-arrow" />
        </div>
        <div class="cra-datetime">
          <span class="cra-date">{{ dateHandler.convertUnixTimestampToHumanDate(craEvent.date_end) }}</span>
          <span class="cra-time">{{ dateHandler.convertUnixTimestampToHumanTime(craEvent.date_end) }}</span>
        </div>
        <v-chip class="cra-day-chip" size="x-small" variant="outlined" :color="eventColor">
          {{ craEvent._dayIndex + 1 }}/{{ craEvent._totalDays }}
        </v-chip>
      </div>
      <div class="cra-meta">
        <v-chip size="small" :color="eventColor" variant="flat">
          {{ craEvent.planning_cra_event_type?.label }}
        </v-chip>
        <v-icon
          v-if="craEvent.status === 'approved'"
          :icon="mdiCheckCircle"
          color="green"
          size="small"
        />
        <!-- Edit and Delete buttons: only when status is "request" -->
        <template v-if="canEditOrDelete">
          <v-btn
            icon
            variant="text"
            size="x-small"
            :title="$t('event.edit.btn.title')"
            @click="openEditModal"
          >
            <v-icon :icon="mdiPencil" size="small" />
          </v-btn>
          <v-btn
            icon
            variant="text"
            size="x-small"
            color="error"
            :title="$t('event.delete.btn.title')"
            @click="confirmDelete"
            :loading="deleting"
          >
            <v-icon :icon="mdiDelete" size="small" />
          </v-btn>
        </template>
      </div>
    </v-card-title>
  </v-card>

  <!-- Edit modal -->
  <event-edit-modal ref="editModalRef" :craEvent="craEvent" @updated="$emit('updated')" />

  <!-- Delete confirmation dialog -->
  <v-dialog v-model="deleteDialog" max-width="400">
    <v-card>
      <v-card-title>{{ $t('event.delete.confirm.title') }}</v-card-title>
      <v-card-text>{{ $t('event.delete.confirm.message') }}</v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="deleteDialog = false">{{ $t('global.btn.cancel') }}</v-btn>
        <v-btn color="error" variant="flat" @click="deleteEvent" :loading="deleting">
          {{ $t('event.delete.confirm.btn') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Offline error snackbar -->
  <v-snackbar v-model="showOfflineError" color="warning" timeout="3000">
    {{ errorMessage }}
  </v-snackbar>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { mdiArrowRight, mdiCheckCircle, mdiPencil, mdiDelete } from '@mdi/js';
import { useI18n } from 'vue-i18n';
import { useApplicationStore } from '@/stores/applicationStore';
import { useStoreManager } from '@/stores/storeManager';
import dateHandler from '@/utils/dateHandler';
import eventApiService from '@/services/eventApi';
import EventEditModal from './EventEditModal.vue';

const { t } = useI18n();
const applicationStore = useApplicationStore();
const storeManager = useStoreManager();

const emit = defineEmits(['updated']);

const props = defineProps({
  craEvent: {
    type: Object,
    required: true,
  },
});

const editModalRef = ref(null);
const deleteDialog = ref(false);
const deleting = ref(false);
const errorMessage = ref(null);
const showOfflineError = ref(false);

watch(errorMessage, (val) => {
  if (val) showOfflineError.value = true;
});
watch(showOfflineError, (val) => {
  if (!val) errorMessage.value = null;
});

const eventColor = computed(() => {
  const now = Math.floor(Date.now() / 1000);
  const type = props.craEvent.planning_cra_event_type;
  return props.craEvent.date_start > now
    ? (type?.color_futur || '#9e9e9e')
    : (type?.color_past || '#9e9e9e');
});

/**
 * Only allow edit/delete when event status is "request" (or null, which also means request).
 */
const canEditOrDelete = computed(() => {
  const status = props.craEvent.status;
  return status === 'request' || status === null;
});

function openEditModal() {
  if (!navigator.onLine || applicationStore.airplaneMode) {
    errorMessage.value = t('event.edit.modal.offline-error');
    return;
  }
  errorMessage.value = null;
  editModalRef.value?.open();
}

function confirmDelete() {
  if (!navigator.onLine || applicationStore.airplaneMode) {
    errorMessage.value = t('event.delete.confirm.offline-error');
    return;
  }
  errorMessage.value = null;
  deleteDialog.value = true;
}

async function deleteEvent() {
  deleting.value = true;
  try {
    const eventId = props.craEvent.id;
    await eventApiService.deleteEvent(eventId);

    // Remove from the store
    const storeKey = props.craEvent._storeKey;
    if (storeKey) {
      storeManager.hardDelete('mission', 'planning_cras', storeKey);

      // Force reactivity hack: reassign planning_cras so Vue detects the change
      const missionStore = storeManager.storeReferences.mission;
      const userId = applicationStore.userDetails?.id;
      if (missionStore.data[userId]?.planning_cras) {
        missionStore.data[userId].planning_cras = { ...missionStore.data[userId].planning_cras };
      }
    }

    deleteDialog.value = false;
    emit('updated');
  } catch (err) {
    console.error('Error deleting event:', err);
    deleteDialog.value = false;
  } finally {
    deleting.value = false;
  }
}
</script>

<style scoped>
.cra-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.cra-range {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex: 1 1 24rem;
}

.cra-datetime {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0.2rem;
}

.cra-datetime.cra-first {
  align-items: end;
}

.cra-date {
  font-weight: 600;
  line-height: 1.2;
}

.cra-time {
  font-weight: 600;
  font-size: 1rem;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.cra-arrow-wrapper {
  display: flex;
  align-items: center;
  align-self: stretch;
}

.cra-arrow {
  margin: 0 0.125rem;
}

.cra-day-chip {
  margin-left: 0.25rem;
}

.cra-meta {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}

@media (max-width: 640px) {
  .cra-meta {
    width: 100%;
    margin-left: 0;
  }
}
</style>

